import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  TextField,
  IconButton,
  CircularProgress,
  Snackbar,
  Stack,
  Alert,
  Button
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import KeypadButton from '../components/KeypadButton';
import { ReactComponent as CinchLogo } from '../cinch.svg';
import { formattedPhoneNumber } from '../shared/utils';
import DeviceService from '../services/deviceService';
import MemberHeader from '../components/MemberHeader';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useMember } from '../contexts/MemberContext';
import KeypadContainer from '../components/KeypadContainer';
import CompanyLogo from '../components/CompanyLogo';

/**
 * Phone entry page for customers to input their mobile number
 * Features a numeric keypad and phone number display
 */
const PhoneEntryPage = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const { showError } = useSnackbar();
  const { setMember } = useMember();
  const navigate = useNavigate();

  // Handle keypad button press
  const handleKeyPress = (value) => {
    // Only add digit if we have less than 10 digits
    if (phoneNumber.replace(/\D/g, '').length < 10) {
      setPhoneNumber(prev => prev + value);
    }
  };

  // Handle phone number submission
  const handleSubmit = async () => {
    const digits = phoneNumber.replace(/\D/g, '');

    // Validate phone number
    if (digits.length !== 10) {
      showError('Please enter a valid 10-digit phone number');
      return;
    }

    setLoading(true);

    try {
      const response = await DeviceService.getMemberByPhone(digits);

      // If member exists and is in group, store in context and go to member dashboard
      if (response && response.is_in_group === true) {
        // Store the member data in the context for use in member dashboard
        setMember(response);
        navigate('/member-dashboard');
      } else {
        // If member is not in group or is a new member, show marketing signup page
        navigate('/marketing-signup', {
          state: {
            phoneNumber: formattedPhoneNumber(phoneNumber),
            rawPhoneNumber: digits
          }
        });
      }
    } catch (err) {
      console.log(err);
      // Handle 404 (member not found) by redirecting to marketing signup
      if (err.status === 404) {
        navigate('/marketing-signup', {
          state: {
            phoneNumber: formattedPhoneNumber(phoneNumber),
            rawPhoneNumber: digits
          }
        });
      } else {
        // Handle other errors
        showError(err.message || 'Failed to verify phone number. Please try again.');
      }
    }

    setLoading(false);
  };

  return (
    <Container sx={{
      height: '100vh',
      width: '100vw',
      maxWidth: '100vw !important',
      display: 'flex',
      flexDirection: 'row',
      padding: '0 !important',
      margin: '0 !important',
    }}>
      <Grid container sx={{
        height: '100%',
        width: '100%'
      }}>
        <Grid item size={6} sx={{
          justifyContent: 'center',
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'row',
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: '100%',
            width: '100%',
            px: 3
          }}>
            <MemberHeader showLock={false} />

            <Box sx={{ maxWidth: '800px', width: '100%' }}>
              <Typography
                variant="h4"
                component="h1"
                gutterBottom
                align="center"
                fontWeight="bold"
                sx={{ mb: 6, mt: 6 }}
              >
                Please Enter Your Mobile Phone Number
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                value={formattedPhoneNumber(phoneNumber)}
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontSize: '2rem',
                    textAlign: 'center',
                    input: { textAlign: 'center' }
                  },
                  endAdornment: loading && <CircularProgress size={24} />
                }}
              />
            </Box>
            <Box pb={2} display="flex" justifyContent="center" alignItems="center">
              <Typography variant="body3" color="text.secondary">
                powered by
              </Typography>
              <Box ml={1} display="flex" alignItems="center">
                <CinchLogo
                  width={75}
                  style={{ marginRight: '4px' }}
                />
              </Box>
            </Box>
          </Box>
        </Grid>
        <Grid item size={6}>
          <KeypadContainer
            loading={loading}
            canSubmit={phoneNumber.replace(/\D/g, '').length === 10}
            onKeyPress={handleKeyPress}
            onSubmit={handleSubmit}
            onBackspace={() => setPhoneNumber(prev => prev.slice(0, -1))}
          />
        </Grid>
      </Grid>
    </Container>
  );

  return (
    <Container maxWidth="lg" sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      py: 4
    }}>
      <MemberHeader showLock={false} />

      {/* Main content area */}
      <Box flexGrow={1}>
        <Grid container spacing={3} sx={{ height: '100%' }}>
          {/* Left side - Title and input field */}
          <Grid item size={6} sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            px: 3
          }}>

            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              align="center"
              fontWeight="bold"
              sx={{ mb: 6 }}
            >
              Please Enter Your Mobile Phone Number
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              value={formattedPhoneNumber(phoneNumber)}
              InputProps={{
                readOnly: true,
                sx: {
                  fontSize: '2rem',
                  textAlign: 'center',
                  input: { textAlign: 'center' }
                },
                endAdornment: loading && <CircularProgress size={24} />
              }}
            />
          </Grid>

          {/* Right side - Numeric keypad */}
          <Grid item size={6} sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: 1,
                width: 'fit-content'
              }}>
                {/* Row 1 */}
                <KeypadButton value={1} onClick={handleKeyPress} disabled={loading} />
                <KeypadButton value={2} onClick={handleKeyPress} disabled={loading} />
                <KeypadButton value={3} onClick={handleKeyPress} disabled={loading} />

                {/* Row 2 */}
                <KeypadButton value={4} onClick={handleKeyPress} disabled={loading} />
                <KeypadButton value={5} onClick={handleKeyPress} disabled={loading} />
                <KeypadButton value={6} onClick={handleKeyPress} disabled={loading} />

                {/* Row 3 */}
                <KeypadButton value={7} onClick={handleKeyPress} disabled={loading} />
                <KeypadButton value={8} onClick={handleKeyPress} disabled={loading} />
                <KeypadButton value={9} onClick={handleKeyPress} disabled={loading} />

                {/* Row 4 */}
                <KeypadButton value={'backspace'} onClick={handleKeyPress} disabled={loading} />
                <KeypadButton value={0} onClick={handleKeyPress} disabled={loading} />
                <KeypadButton value={'enter'} onClick={handleKeyPress} disabled={loading || phoneNumber.replace(/\D/g, '').length !== 10} />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Footer */}
      <Box pb={2} display="flex" justifyContent="center" alignItems="center">
        <Typography variant="body3" color="text.secondary">
          powered by
        </Typography>
        <Box ml={1} display="flex" alignItems="center">
          <CinchLogo
            width={75}
            style={{ marginRight: '4px' }}
          />
        </Box>
      </Box>
    </Container>
  );
};

export default PhoneEntryPage;